import { ReactNode, Suspense, lazy, useEffect } from 'react'
import {
  Route,
  Routes,
  Outlet,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { Toaster } from 'react-hot-toast'

import { useAuth } from 'ctx/AuthContext'
import { useUI } from 'ctx/UIContext'

import LoadingScreen from 'components/layout/LoadingScreen'

const ForgotPassword = lazy(() => import('pages/Auth/ForgotPassword'))
const ResetPassword = lazy(() => import('pages/Auth/ResetPassword'))
const Login = lazy(() => import('pages/Auth/Login'))
const Signup = lazy(() => import('pages/Auth/Signup'))
const ConfirmSignup = lazy(() => import('pages/Auth/ConfirmSignup'))
const MainDashboard = lazy(() => import('pages/Dashboard'))
const AllSubmissions = lazy(() => import('pages/Dashboard/AllSubmissions'))
const Analytics = lazy(() => import('pages/Dashboard/Analytics'))
const AccountSettings = lazy(() => import('pages/Common/AccountSettings'))
const Unsubscribe = lazy(() => import('pages/Common/Unsubscribe'))

import 'react-tooltip/dist/react-tooltip.css'

function App() {
  const ui = useUI()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (location.pathname === '/') {
      navigate('/analytics')
    }
  }, [location])

  return (
    <>
      <Routes>
        <Route path='/login' element={<Login />} />
        <Route
          path='/signup'
          element={
            <SuspensedView>
              <Signup />
            </SuspensedView>
          }
        />
        <Route
          path='/forgot-password'
          element={
            <SuspensedView>
              <ForgotPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/reset-password'
          element={
            <SuspensedView>
              <ResetPassword />
            </SuspensedView>
          }
        />
        <Route
          path='/confirm-signup'
          element={
            <SuspensedView>
              <ConfirmSignup />
            </SuspensedView>
          }
        />
        <Route
          path='/unsubscribe-confirm'
          element={
            <SuspensedView>
              <Unsubscribe />
            </SuspensedView>
          }
        />
        <Route path='/' element={<PrivateRoute />}>
          <Route
            path='/'
            element={
              <SuspensedView>
                <MainDashboard />
              </SuspensedView>
            }
          >
            <Route
              path='all-submissions'
              element={
                <SuspensedView>
                  <AllSubmissions />
                </SuspensedView>
              }
            />
            <Route
              path='analytics'
              element={
                <SuspensedView>
                  <Analytics />
                </SuspensedView>
              }
            />

            <Route
              path='account-settings'
              element={
                <SuspensedView>
                  <AccountSettings />
                </SuspensedView>
              }
            />
          </Route>
        </Route>
      </Routes>
      <Toaster
        toastOptions={{
          duration: 6000,
          position: 'bottom-center',
          // Styling
          style: {
            borderRadius: '10px',
            color: '#eee',
          },
          className: 'rounded-full !bg-primary-purple',
        }}
      />
      {ui.isLoading ? <LoadingScreen isLoading /> : null}
    </>
  )
}
function PrivateRoute() {
  const auth = useAuth()
  return auth?.user ? <Outlet /> : <Navigate to='/login' />
}

const SuspensedView = ({ children }: { children: ReactNode }) => {
  return <Suspense fallback={<LoadingScreen isLoading />}>{children}</Suspense>
}
export default App
